import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { 
  MessageSquare, 
  TrendingUp, 
  Clock,
  Users,
  Mail,
  Phone,
  FileText,
  Loader2,
  RefreshCw,
  Download,
  Calendar
} from 'lucide-react';
import { format, subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#8b5cf6', '#06b6d4', '#22c55e', '#f59e0b', '#ef4444', '#ec4899', '#6366f1'];

const TemplateAnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [analytics, setAnalytics] = useState({
    totalSent: 0,
    totalTemplates: 0,
    topTemplates: [],
    usageByType: [],
    usageOverTime: [],
    sentByUser: [],
    responseRates: [],
    averageResponseTime: null
  });

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    setRefreshing(true);
    try {
      // Fetch templates
      const templatesRes = await axios.get(`${API}/message-templates`);
      const templates = templatesRes.data.templates || [];

      // Fetch communications for analytics
      const commsRes = await axios.get(`${API}/communications`, {
        params: { limit: 1000 }
      });
      const communications = commsRes.data.communications || [];

      // Calculate date range
      const now = new Date();
      let startDate;
      switch (dateRange) {
        case '7d':
          startDate = subDays(now, 7);
          break;
        case '30d':
          startDate = subDays(now, 30);
          break;
        case '90d':
          startDate = subDays(now, 90);
          break;
        case 'year':
          startDate = subMonths(now, 12);
          break;
        default:
          startDate = subDays(now, 30);
      }

      // Filter communications by date range
      const filteredComms = communications.filter(c => {
        const commDate = new Date(c.created_at || c.timestamp);
        return commDate >= startDate && commDate <= now;
      });

      // Calculate top templates usage
      const templateUsage = {};
      filteredComms.forEach(comm => {
        const templateId = comm.template_id || comm.template_name || 'No Template';
        templateUsage[templateId] = (templateUsage[templateId] || 0) + 1;
      });

      const topTemplates = Object.entries(templateUsage)
        .map(([name, count]) => {
          const template = templates.find(t => t.id === name || t.name === name);
          return {
            name: template?.name || name,
            count,
            type: template?.type || 'unknown',
            lastUsed: template?.updated_at || null
          };
        })
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Usage by type (SMS, Email, Call Script)
      const typeCount = { sms: 0, email: 0, call_script: 0 };
      filteredComms.forEach(comm => {
        const type = comm.type || comm.channel || 'sms';
        if (typeCount[type] !== undefined) {
          typeCount[type]++;
        }
      });

      const usageByType = [
        { name: 'SMS', value: typeCount.sms, color: '#22c55e' },
        { name: 'Email', value: typeCount.email, color: '#6366f1' },
        { name: 'Call Script', value: typeCount.call_script, color: '#f59e0b' }
      ];

      // Usage over time (daily for last 30 days or weekly/monthly for longer ranges)
      const usageOverTime = [];
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365;
      const groupBy = days <= 30 ? 'day' : days <= 90 ? 'week' : 'month';

      for (let i = 0; i < (groupBy === 'day' ? days : groupBy === 'week' ? Math.ceil(days / 7) : 12); i++) {
        const date = groupBy === 'day' 
          ? subDays(now, days - 1 - i)
          : groupBy === 'week'
          ? subDays(now, days - 1 - i * 7)
          : subMonths(now, 11 - i);
        
        const label = groupBy === 'day' 
          ? format(date, 'MMM d')
          : groupBy === 'week'
          ? `Week ${i + 1}`
          : format(date, 'MMM yyyy');

        // Count communications for this period
        let count = 0;
        filteredComms.forEach(comm => {
          const commDate = new Date(comm.created_at || comm.timestamp);
          if (groupBy === 'day') {
            if (format(commDate, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')) {
              count++;
            }
          } else if (groupBy === 'week') {
            const weekStart = subDays(date, 6);
            if (commDate >= weekStart && commDate <= date) {
              count++;
            }
          } else {
            if (format(commDate, 'yyyy-MM') === format(date, 'yyyy-MM')) {
              count++;
            }
          }
        });

        usageOverTime.push({ date: label, count });
      }

      // Sent by user
      const userSends = {};
      filteredComms.forEach(comm => {
        const userName = comm.sent_by_name || comm.created_by_name || 'Unknown';
        userSends[userName] = (userSends[userName] || 0) + 1;
      });

      const sentByUser = Object.entries(userSends)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8);

      setAnalytics({
        totalSent: filteredComms.length,
        totalTemplates: templates.length,
        topTemplates,
        usageByType,
        usageOverTime,
        sentByUser,
        responseRates: [], // Would need response tracking data
        averageResponseTime: null // Would need response tracking data
      });

    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleExport = () => {
    // Export analytics data as CSV
    const csvData = [
      ['Template Analytics Report', '', ''],
      ['Generated:', format(new Date(), 'PPpp'), ''],
      ['Date Range:', dateRange, ''],
      ['', '', ''],
      ['Top Templates', '', ''],
      ['Name', 'Usage Count', 'Type'],
      ...analytics.topTemplates.map(t => [t.name, t.count, t.type]),
      ['', '', ''],
      ['Usage by Type', '', ''],
      ['Type', 'Count', ''],
      ...analytics.usageByType.map(t => [t.name, t.value, '']),
      ['', '', ''],
      ['Sent by User', '', ''],
      ['User', 'Count', ''],
      ...analytics.sentByUser.map(u => [u.name, u.count, ''])
    ];

    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `template-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success('Report exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="template-analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Template Analytics</h1>
            <p className="text-muted-foreground">Usage statistics and performance metrics for message templates</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40" data-testid="date-range-select">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="year">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAnalytics} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Total Messages Sent
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{analytics.totalSent}</div>
            <p className="text-sm text-muted-foreground mt-1">In selected period</p>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Active Templates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{analytics.totalTemplates}</div>
            <p className="text-sm text-muted-foreground mt-1">Available for use</p>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Active Users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">{analytics.sentByUser.length}</div>
            <p className="text-sm text-muted-foreground mt-1">Sent messages</p>
          </CardContent>
        </Card>

        <Card className="card-shadow">
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Avg. Daily
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">
              {Math.round(analytics.totalSent / (dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : dateRange === '90d' ? 90 : 365))}
            </div>
            <p className="text-sm text-muted-foreground mt-1">Messages per day</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage Over Time */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Usage Over Time</CardTitle>
            <CardDescription>Messages sent during the selected period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics.usageOverTime}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }} 
                    className="text-muted-foreground"
                  />
                  <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#8b5cf6" 
                    strokeWidth={2}
                    dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                    name="Messages"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Usage by Type */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Usage by Type</CardTitle>
            <CardDescription>Distribution of message channels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.usageByType}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {analytics.usageByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Templates */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Top Templates</CardTitle>
            <CardDescription>Most frequently used message templates</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.topTemplates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No template usage data available</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Uses</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analytics.topTemplates.map((template, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {template.type === 'sms' && <Phone className="w-3 h-3 mr-1" />}
                          {template.type === 'email' && <Mail className="w-3 h-3 mr-1" />}
                          {template.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">{template.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Sent by User */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle>Activity by User</CardTitle>
            <CardDescription>Messages sent by each team member</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.sentByUser.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No user activity data available</p>
              </div>
            ) : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.sentByUser} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      tick={{ fontSize: 12 }} 
                      width={100}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Messages" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TemplateAnalyticsPage;
